import React from 'react';
import DatePicker from 'react-datepicker';
import { Link } from 'react-router';
import useFileNameLength from '../../../../hooks/useFileNameLength';
import { DOCUMENT_CONFIGS } from '../../../../models/superadmin';
import { TUploadedDocumentsData } from '../../../../store/onboarding/types';
import { ICandidateDetails } from '../../../../store/superadmin/types';
import {
  completionDateDocTypes,
  formatDateToReadable,
  getDateLabel,
  getDocumentDate,
  getDocumentName,
  getMinDateByDocumentType,
  getValidityMonths,
  isDateExpired,
  isIssuedType,
  isOneMonthBefore,
  issueDateDocTypes,
  validDateDocTypes
} from '../../../../utils/documentHelper';
import { isFirstAidOptional } from '../../../../utils/missingDocsHelper';
import { formatFileName } from '../../../../utils/stringUtils';
import CustomButton from '../../../UI/Buttons/CustomButton';
import Download from '../../../UI/Icons/Download';
import Warning from '../../../UI/Icons/Warning';
import CustomDateInput from '../../../UI/Inputs/DateInput';
import DocumentUpload from '../../../UI/Inputs/DocumentUpload';
import ErrorMessage from '../../../UI/Tooltips/ErrorMessage';

interface KeyDocumentsSectionProps {
  reviewCandidateData: ICandidateDetails | null;
  candidateVerificationStatus: string | undefined;
  candidateDocumentList: TUploadedDocumentsData[];
  expiringDocumentsCollections: any[];
  documentDates: Record<string, Date | null>;
  handleFileSelect: (file: any, type: string) => void;
  handleDocDate: (type: string, val: Date | null) => Promise<void>;
  alternativePositions: string[];
}

const KeyDocumentsSection: React.FC<KeyDocumentsSectionProps> = ({
  reviewCandidateData,
  candidateVerificationStatus,
  candidateDocumentList,
  expiringDocumentsCollections,
  documentDates,
  handleFileSelect,
  handleDocDate,
  alternativePositions
}) => {
  const qualificationDocs = candidateDocumentList
    ?.filter(
      (doc) =>
        !["police_check", "wcc_compliance_check", "right_to_work", "reference_check", "reference_check_1", "section_b_compliance_letter",].includes(
          doc.document_type
        )
    )

  const workingRights = reviewCandidateData?.profile?.working_rights;
  const qualification = reviewCandidateData?.qualifications?.qualification;
  const acecqa_approved = reviewCandidateData?.qualifications?.acecqa_approved === 1;
  const { maxLength, preserveEndChars } = useFileNameLength(false);


  // Define required documents based on conditions
  const requiredDocs: { condition: boolean; type: string }[] = [
    { condition: acecqa_approved, type: "ACECQA_assessment" },
    {
      condition: alternativePositions.includes("Chef/Cook"),
      type: "food_handler_certificate",
    },
    {
      condition: alternativePositions.includes("Chef/Cook"),
      type: "cooking_qualification",
    },
    {
      condition: qualification === "post_graduate_diploma",
      type: "post_graduate_diploma",
    },
    {
      condition: qualification === "bachelor_or_degree",
      type: "bachelor_or_degree",
    },
    {
      condition: qualification === "diploma",
      type: "diploma",
    },
    {
      condition: ["post_graduate_diploma", "bachelor_or_degree"].includes(
        qualification ?? ""
      ),
      type: "teachers_registration",
    },
    {
      condition: qualification === "certificate_iii",
      type: "certificate_iii",
    },
    {
      condition: qualification === "currently_studying",
      type: "course_progression_letter",
    },
    {
      condition: workingRights === "australian_permanent_resident",
      type: "permanent_residency_visa",
    },
    {
      condition: workingRights === "australian_visa_holder",
      type: "australian_work_visa",
    },
    // Always required documents
    {
      condition: true,
      type: "working_with_children_check",
    },
    {
      condition: !isFirstAidOptional(reviewCandidateData?.qualifications),
      type: "first_aid_certificate",
    },
    {
      condition: true,
      type: "mandatory_reporting_training",
    }
  ];

  // Get list of required document types that should be present
  const requiredDocTypes = new Set(
    requiredDocs
      .filter((doc) => doc.condition)
      .map((doc) => doc.type)
  );

  const getFileName = (type: string) => {
    const reviewDocument = candidateDocumentList.find(
      (file: TUploadedDocumentsData) => file.document_type === type
    );
    return reviewDocument?.file_name || "";
  };

  /**
   * Check if a document is expired based on document_type using expiringDocumentsCollections
   * @param documentType - The type of document to check
   * @returns boolean - true if document is expired, false otherwise
   */
  const isDocumentExpired = (documentType: string): boolean => {
    if (!expiringDocumentsCollections) return false;
    const document = expiringDocumentsCollections.find(
      (doc) => doc.document_type === documentType
    );
    if (!document) return false;
    return document.is_expired === 1;
  };

  // Utility to get document by type
  const getDocumentByType = (type: string) =>
    candidateDocumentList.find((file: any) => file.document_type === type);

  // Helper to get document and file name by type
  const getDocumentData = (type: string) => {
    const document = getDocumentByType(type);
    return document?.file_path
  };

  const wcc_valid_until_str = getDocumentByType("working_with_children_check")?.validity_date;
  const checkWCCGoingToExpire = wcc_valid_until_str && isOneMonthBefore(wcc_valid_until_str);
  const checkWCCExpired = wcc_valid_until_str && isDateExpired(wcc_valid_until_str);

  const teachers_registration_valid_until_str = getDocumentByType("teachers_registration")?.validity_date;
  const checkTRGoingToExpire = teachers_registration_valid_until_str && isOneMonthBefore(teachers_registration_valid_until_str);
  const checkTRExpired = teachers_registration_valid_until_str && isDateExpired(teachers_registration_valid_until_str);

  const australian_work_visa_valid_until_str = getDocumentByType("australian_work_visa")?.validity_date;
  const checkWorkVisaGoingToExpire = australian_work_visa_valid_until_str && isOneMonthBefore(australian_work_visa_valid_until_str);
  const checkWorkVisaExpired = australian_work_visa_valid_until_str && isDateExpired(australian_work_visa_valid_until_str);

  // For non-verified status - show upload interface
  if (candidateVerificationStatus !== 'verified') {
    // Filter DOCUMENT_CONFIGS to only show required documents or uploaded documents
    const filteredDocConfigs = DOCUMENT_CONFIGS.filter((config) => {
      try {
        const hasFile = getFileName(config?.type) !== "";
        const isRequired = requiredDocTypes.has(config?.type);
        const showOnlyForm = Array.isArray(config.showOnlyForm) ? config.showOnlyForm : [];
        const shouldShow =
          showOnlyForm.length === 0 ||
          showOnlyForm.includes(qualification ?? "") ||
          showOnlyForm.includes(acecqa_approved ? "acecqa_approved" : "") ||
          showOnlyForm.some((item) => alternativePositions.includes(item)) ||
          showOnlyForm.includes(workingRights ?? '');

        const willShow = isRequired || (hasFile && shouldShow);

        // Show if required (always), OR if it has a file AND meets the shouldShow criteria
        return willShow;
      } catch (error) {
        console.error('Error filtering config:', config.type, error);
        return false;
      }
    });

    return (
      <>
        {filteredDocConfigs.length > 0 && (
            <>
              <div className="bg-off-white min-h-1px"></div>
              <div className="fs-22">Key Documents</div>
            </>
        )}

        {filteredDocConfigs.map((config, idx, filteredList) => {
          const hasFile = getFileName(config?.type) !== "";

          return (
            <div className="d-flex flex-column gap-10" key={config?.type}>
              <div className="fs-16 fw-700">{config?.title}</div>
              
              {hasFile ? (
                <>
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={config?.type}
                    candidate_uuid={reviewCandidateData?.uuid}
                    uploadedFileName={getFileName(config?.type)}
                    fileUrl={getDocumentData(config?.type) || null}
                    removeIcon={true}
                  />
                  {config?.type !== "ACECQA_assessment" && config?.type !== "permanent_residency_visa" && config?.type !== "course_progression_letter" && (
                    <div className="d-flex gap-10 align-items-center">
                      <div className={`fs-13 fw-400 text-grey-dark ${completionDateDocTypes.includes(config?.type) ? '' : 'text-nowrap'}`}>
                        {getDateLabel(config?.type)}:{" "}
                      </div>                       
                      <div className="min-w-200px">
                        <DatePicker
                          selected={config?.type ? documentDates[config.type] ?? null : null}
                          onChange={(date: Date | null) => {
                            handleDocDate(config?.type, date);
                          }}
                          customInput={
                            <CustomDateInput
                              placeholder="Select a date..."
                            />
                          }
                          dateFormat="dd MMM yyyy"
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={completionDateDocTypes.includes(config?.type) ? 50 : 15}
                          minDate={validDateDocTypes.includes(config?.type) ? new Date() : getMinDateByDocumentType(config?.type)}
                          maxDate={[...issueDateDocTypes, ...completionDateDocTypes].includes(config?.type) ? new Date() : undefined}
                        />
                      </div>
                       
                      <div className="d-none d-md-block">
                          {issueDateDocTypes.includes(config?.type) && (
                            <>
                              {(() => {
                                const isExpired = isDocumentExpired(config?.type);

                                if (isExpired) {
                                  return (
                                    <div className="d-flex flex-row fs-13 fw-400 gap-3 text-alert">
                                      <Warning
                                        width={"15"}
                                        height={"15"}
                                        color={"#e52514"}
                                      />
                                      Expired: employee has been requested to
                                      upload a valid document in order to
                                      search and accept jobs on Connected Crew
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                                      <Warning
                                        width={"15"}
                                        height={"15"}
                                        color={"#464646"}
                                      />
                                      Expires{" "}
                                      {getValidityMonths(config?.type)} months
                                      after issue date and will need renewing
                                    </div>
                                  );
                                }
                              })()}
                            </>
                          )}
                          {config?.type === "working_with_children_check" &&
                            checkWCCGoingToExpire &&
                            !checkWCCExpired && (
                              <ErrorMessage
                                className="align-items-center gap-3 lh-1"
                                message="Expires in less than a month"
                              />
                            )}

                          {config?.type === "australian_work_visa" &&
                            checkWorkVisaGoingToExpire &&
                            !checkWorkVisaExpired && (
                              <ErrorMessage
                                className="align-items-center gap-3 lh-1"
                                message="Expires in less than a month"
                              />
                            )}

                          {config?.type === "teachers_registration" &&
                            checkTRGoingToExpire &&
                            !checkTRExpired && (
                              <ErrorMessage
                                className="align-items-center gap-3 lh-1"
                                message="Expires in less than a month"
                              />
                            )}

                          {config?.type === "working_with_children_check" &&
                            checkWCCGoingToExpire &&
                            checkWCCExpired && (
                              <ErrorMessage
                                className="max-w-335px align-items-center gap-3 lh-1"
                                message="Expired: employee will be requested to upload a valid document if Stage 3 approval is granted."
                              />
                            )}

                          {config?.type === "teachers_registration" &&
                            checkTRGoingToExpire &&
                            checkTRExpired && (
                              <ErrorMessage
                                className="max-w-335px align-items-center gap-3 lh-1"
                                message="Expired: employee will be requested to upload a valid document if Stage 3 approval is granted."
                              />
                            )}

                          {config?.type === "australian_work_visa" &&
                            checkWorkVisaGoingToExpire &&
                            checkWorkVisaExpired && (
                              <ErrorMessage
                                className="max-w-335px align-items-center gap-3 lh-1"
                                message="Expired: employee will be requested to upload a valid document if Stage 3 approval is granted."
                              />
                            )}
                        </div>
                      </div>
                  )}
                  <div className="d-md-none">
                    {issueDateDocTypes.includes(config?.type) && (
                      <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                        <Warning
                          width={"15"}
                          height={"15"}
                          color={"#464646"}
                        />
                        Expires {getValidityMonths(config?.type)} months
                        after issue date and will need renewing
                      </div>
                    )}
                    {config?.type === "working_with_children_check" &&
                      checkWCCGoingToExpire &&
                      !checkWCCExpired && (
                        <ErrorMessage
                          className="align-items-center gap-3 lh-1"
                          message="Expires in less than a month"
                        />
                      )}

                    {config?.type === "teachers_registration" &&
                      checkTRGoingToExpire &&
                      !checkTRExpired && (
                        <ErrorMessage
                          className="align-items-center gap-3 lh-1"
                          message="Expires in less than a month"
                        />
                      )}

                    {config?.type === "australian_work_visa" &&
                      checkWorkVisaGoingToExpire &&
                      !checkWorkVisaExpired && (
                        <ErrorMessage
                          className="align-items-center gap-3 lh-1"
                          message="Expires in less than a month"
                        />
                      )}

                    {config?.type === "working_with_children_check" &&
                      checkWCCGoingToExpire &&
                      checkWCCExpired && (
                        <ErrorMessage
                          className="max-w-335px align-items-center gap-3 lh-1"
                          message="Expired: employee will be requested to upload a valid document if Stage 3 approval is granted."
                        />
                      )}

                    {config?.type === "teachers_registration" &&
                      checkTRGoingToExpire &&
                      checkTRExpired && (
                        <ErrorMessage
                          className="max-w-335px align-items-center gap-3 lh-1"
                          message="Expired: employee will be requested to upload a valid document if Stage 3 approval is granted."
                        />
                      )}

                    {config?.type === "australian_work_visa" &&
                      checkWorkVisaGoingToExpire &&
                      checkWorkVisaExpired && (
                        <ErrorMessage
                          className="max-w-335px align-items-center gap-3 lh-1"
                          message="Expired: employee will be requested to upload a valid document if Stage 3 approval is granted."
                        />
                      )}
                  </div>
                </>
              ) : (
                <div className="fs-13 fw-400 text-grey-medium">
                  Document Not Uploaded
                </div>
              )}

              {idx < filteredList.length - 1 && (
                <div className="bg-off-white min-h-1px"></div>
              )}
            </div>
          );
        })}
      </>
    );
  }

  // For verified status - show download interface
  // Get all required document configs
  const requiredDocConfigs = DOCUMENT_CONFIGS.filter((config) => {
    return requiredDocTypes.has(config.type);
  });

  // Create a map of uploaded documents by type
  const uploadedDocsMap = new Map(
    qualificationDocs.map(doc => [doc.document_type, doc])
  );

  // Combine required docs with their uploaded status
  const allDocsToShow = requiredDocConfigs.map(config => {
    const uploadedDoc = uploadedDocsMap.get(config.type);
    return {
      config,
      uploadedDoc,
      hasFile: !!uploadedDoc
    };
  });

  // Also include any uploaded docs that aren't in the required list
  qualificationDocs.forEach(doc => {
    if (!requiredDocTypes.has(doc.document_type)) {
      const config = DOCUMENT_CONFIGS.find(c => c.type === doc.document_type);
      if (config) {
        allDocsToShow.push({
          config,
          uploadedDoc: doc,
          hasFile: true
        });
      }
    }
  });

  return (
    <>
      {allDocsToShow.length > 0 && (
        <>
        <div className="bg-off-white min-h-1px"></div>
        <div className="fs-22">Key Documents</div>
          </>
      )}
      {allDocsToShow.map(({ config, uploadedDoc, hasFile }, idx) => {
        if (!hasFile) {
          // Show "Document Not Uploaded" for missing required documents
          return (
            <React.Fragment key={config.type}>
              <div className="d-flex flex-column gap-10">
                <div className="fs-13 fw-700 text-black text-capitalize">
                  {config.title}
                </div>
                <div className="fs-13 fw-400 text-grey-medium">
                  Document Not Uploaded
                </div>
              </div>
              {idx < allDocsToShow.length - 1 && (
                <div className="bg-off-white min-h-1px"></div>
              )}
            </React.Fragment>
          );
        }

        // Show uploaded document
        const doc = uploadedDoc!;
        const dateClassName = validDateDocTypes.includes(doc.document_type) && isOneMonthBefore(getDocumentDate(doc)) 
          ? 'fs-13 fw-700 text-alert' 
          : 'fs-13 fw-400 text-grey-dark';

        const validityDateClassName = isOneMonthBefore(doc?.validity_date ?? "")
          ? "fw-700 text-alert"
          : "fw-400 text-grey-dark";

        return (
        <React.Fragment key={doc.document_type}>
          <div className="d-flex flex-column gap-10">
            <div className="fs-13 fw-700 text-black text-capitalize">
              {getDocumentName(doc.document_type)}
            </div>
            <div className="d-flex">
              <Link to={doc.file_path} target="_blank" rel="noopener noreferrer">
                <CustomButton
                  variant={"primary"}
                  type="button"
                  className="btn-theme bg-off-white text-black"
                >
                  <Download />
                  {formatFileName(doc.file_name, maxLength, preserveEndChars)}
                </CustomButton>
              </Link>
            </div>
            {doc.document_type !== "ACECQA_assessment" && doc.document_type !== "course_progression_letter" && (
              <div className="d-flex flex-row gap-5">
                {doc.document_type !== "permanent_residency_visa" && (
                  <div className="fs-13 fw-400 text-grey-dark">
                    {getDateLabel(doc.document_type)}:{" "}
                  </div>
                )}
               
                <span className={dateClassName}>
                  {isIssuedType(doc.document_type) ? (
                    <>
                      {doc.document_type !== "permanent_residency_visa" && (formatDateToReadable(doc.issued_date) || "No data")} {doc.document_type !== "permanent_residency_visa" && (<> | Valid until:{" "}
                      <span className={validityDateClassName}>
                        {formatDateToReadable(doc?.validity_date) || "No data"}{" "}{isDateExpired(doc?.validity_date ?? "") ? "(Expired)" : ""}
                      </span></>)}
                    </>
                  ) : (
                    doc.document_type !== "permanent_residency_visa" && (formatDateToReadable(getDocumentDate(doc)) || "No data")
                  )}{" "}
                  {[...validDateDocTypes].includes(doc.document_type) && isDateExpired(doc?.validity_date ?? "")
                    ? "(Expired)"
                    : ""}
                </span>
              </div>
            )}
          </div>
          {idx < allDocsToShow.length - 1 && (
            <div className="bg-off-white min-h-1px"></div>
          )}
        </React.Fragment>
        );
      })}
    </>
  );
};

export default KeyDocumentsSection;
